import {
  useGetAllCoursesQuery,
  useAddCourseMutation,
  useGetCourseByIdQuery,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
} from "../api/courseApi";
import { CoursePayloadType } from "../types/courseRequest";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";
import { showToast } from "../../../../core/utils/alert";
import { PaginationProps } from "../../../../core/data/interface";

export const useCourse = (params?: PaginationProps) => {
  const { data, isLoading, isError, error } = useGetAllCoursesQuery(
    params || {}
  );

  const [
    addCourseMutation,
    { isLoading: isAdding, isError: isAddError, error: addError },
  ] = useAddCourseMutation();

  const [
    updateCourseMutation,
    { isLoading: isUpdating, isError: isUpdateError, error: updateError },
  ] = useUpdateCourseMutation();

  const [
    deleteCourseMutation,
    { isLoading: isDeleting, isError: isDeleteError, error: deleteError },
  ] = useDeleteCourseMutation();

  const paginationData = {
    totalPages: data?.data?.totalPages,
    pageSize: data?.data?.pageSize,
    totalItems: data?.data?.totalItems,
    currentPage: data?.data?.currentPage,
  };

  const errorMessage = isError ? getApiErrorMessage(error) : null;
  const addErrorMessage = isAddError ? getApiErrorMessage(addError) : null;
  const updateErrorMessage = isUpdateError
    ? getApiErrorMessage(updateError)
    : null;
  const deleteErrorMessage = isDeleteError
    ? getApiErrorMessage(deleteError)
    : null;

  const addNewCourse = async (payload: CoursePayloadType) => {
    try {
      const response = await addCourseMutation(payload).unwrap();
      showToast({
        title: "Success",
        text: "Course added successfully!",
        icon: "success",
        timer: 3000,
      });
      return { success: true, data: response.data };
    } catch (error) {
      showToast({
        title: "Error",
        text: addErrorMessage || "Failed to add course",
        icon: "error",
        timer: 3000,
      });
      return {
        success: false,
        error: addErrorMessage || "Failed to add course",
      };
    }
  };

  const useGetCourseById = (id: string) => {
    const { data } = useGetCourseByIdQuery({ id }, { skip: !id });
    return data?.data.course;
  };

  const updateCourse = async (id: string, payload: CoursePayloadType) => {
    try {
      const response = await updateCourseMutation({ id, payload }).unwrap();
      showToast({
        title: "Success",
        text: "Course updated successfully!",
        icon: "success",
        timer: 3000,
      });
      return { success: true, data: response.data };
    } catch (error) {
      showToast({
        title: "Error",
        text: updateErrorMessage || "Failed to update course",
        icon: "error",
        timer: 3000,
      });
      return {
        success: false,
        error: updateErrorMessage || "Failed to update course",
      };
    }
  };

  const deleteCourse = async (id: string) => {
    try {
      const response = await deleteCourseMutation({ id }).unwrap();
      showToast({
        title: "Success",
        text: "Course deleted successfully!",
        icon: "success",
        timer: 3000,
      });
      return { success: true, data: response };
    } catch (error) {
      showToast({
        title: "Error",
        text: deleteErrorMessage || "Failed to delete course",
        icon: "error",
        timer: 3000,
      });
      return {
        success: false,
        error: deleteErrorMessage || "Failed to delete course",
      };
    }
  };

  const getCourseState = () => ({
    courses: data?.data?.items || [],
    isLoading,
    error: errorMessage,
  });

  return {
    getCourseState,
    paginationData,
    useGetCourseById,
    addNewCourse,
    updateCourse,
    deleteCourse,
    isAdding,
    isUpdating,
    isDeleting,
    addError: addErrorMessage,
    updateError: updateErrorMessage,
    deleteError: deleteErrorMessage,
  };
};
